import { useCallback, useState, useEffect, useRef } from 'react';
import { useIntersection } from '@mantine/hooks';

import { useContacts } from '@web/hooks';

import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';

import { Button } from '@web/components/ui/button';
import { ScrollArea } from '@web/components/ui/scroll-area';

import { ContactCard } from './contact-card';

import { cn } from '@web/lib/shadcn';

import { Route } from '..';
import { trpc } from '@web/lib/trpc';
import { Filter, ViewFilters } from '@web/components/ui/view-filters';

export function ContactsList({
  activeContactId,
  setActiveContactId,
  isMobileView = false,
}: {
  activeContactId?: string;
  setActiveContactId: (id: string) => void;
  isMobileView?: boolean;
}) {
  const navigate = Route.useNavigate();

  const { setCreateContactDialogOpen } = Route.useRouteContext();

  const { contacts, fetchMoreContacts } = useContacts();

  const { getContacts: getContactsUtils } = trpc.useUtils().messaging;

  const fetchNextPageTriggerRef = useRef<HTMLDivElement | null>(null);

  const {
    ref: fetchNextPageTriggerObserverRef,
    entry: fetchNextPageTriggerObserver,
  } = useIntersection({
    root: fetchNextPageTriggerRef.current,
    threshold: 0,
  });

  trpc.contact.onSalesPipelineContactStatusUpdated.useSubscription(undefined, {
    onData: (update) => {
      getContactsUtils.setInfiniteData(
        {},

        (prevData) => {
          if (!prevData) return;

          const { pageParams, pages } = prevData;

          return {
            pageParams,

            pages: pages.map((page) =>
              page.map((contact) =>
                contact.id === update.contactId
                  ? { ...contact, status: update.newStatus }
                  : contact,
              ),
            ),
          };
        },
      );
    },
  });

  useEffect(() => {
    if (fetchNextPageTriggerObserver?.isIntersecting) {
      void fetchMoreContacts();
    }
  }, [fetchNextPageTriggerObserver, fetchMoreContacts]);

  const [isContactsExpanded, setIsContactsExpanded] = useState(true);

  const { name } = Route.useSearch();

  const filteredContacts = contacts.filter((contact) =>
    contact.name.toLowerCase().includes((name ?? '').toLowerCase()),
  );

  const handleContactSelect = useCallback(
    (contactId: string) => {
      setActiveContactId(contactId);
    },
    [setActiveContactId],
  );

  const toggleContacts = () => {
    setIsContactsExpanded((state) => !state);
  };

  const filters: Filter[] = [
    {
      key: 'name',
      label: 'Name',
      type: 'string',
      value: name,
      isMainFilter: true,
    },
  ];

  const onFilterChange = (
    newSearch: Record<string, string | number | boolean | object | undefined>,
  ) => {
    void navigate({
      search: (current) => ({ ...current, ...newSearch }),
    });
  };

  return (
    <div
      className={cn(
        'relative z-0 flex flex-col border-r border-gray-200 transition-all duration-300',
        isMobileView
          ? 'w-full' // Take full width on mobile
          : isContactsExpanded
            ? 'w-100'
            : 'w-[60px]',
      )}
    >
      <div className="relative flex h-full flex-col">
        {/* Header with fixed height similar to WhatsApp */}
        <div
          className={cn(
            'sticky top-0 z-10 flex items-center gap-2 border-b bg-gray-100/80 px-4 py-3',
            isMobileView
              ? 'justify-between' // Mobile header always expanded
              : isContactsExpanded
                ? 'justify-between'
                : 'justify-center',
          )}
        >
          {/* App title or user profile section */}
          <div className="flex items-center">
            <h2
              className={cn(
                'text-lg font-semibold',
                !isContactsExpanded && !isMobileView && 'hidden',
              )}
            >
              Chats
            </h2>
          </div>

          {/* Actions section */}
          <div className="flex items-center gap-2">
            {/* Search/filter button */}
            {(isMobileView || isContactsExpanded) && (
              <ViewFilters
                filters={filters}
                onFilterChange={onFilterChange}
                mobileView={true}
              />
            )}

            {/* New chat button (like WhatsApp) */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCreateContactDialogOpen(true);
              }}
              className="h-9 w-9 flex-shrink-0 rounded-full bg-blue-50 p-0 text-blue-600 hover:bg-blue-100"
            >
              <Plus className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Contact list with improved scrolling */}
        <ScrollArea
          className="flex-1 overflow-y-auto"
          showThumb={false}
          viewportClassName="block"
        >
          <div className={cn('flex flex-col', isContactsExpanded)}>
            {/* No contacts message */}
            {filteredContacts.length === 0 && (
              <div className="mx-3 my-4 flex h-60 flex-col items-center justify-center rounded-md border border-gray-100 bg-gray-50 p-6 text-center">
                <div className="mb-3 rounded-full bg-gray-100 p-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                    />
                  </svg>
                </div>
                <p className="mb-1 font-medium text-gray-700">
                  No contacts found
                </p>
                <p className="max-w-[220px] text-xs text-gray-500">
                  Create a new contact to start chatting or try a different
                  search term
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 rounded-full border-blue-100 bg-blue-50 text-blue-600 hover:bg-blue-100"
                  onClick={() => {
                    setCreateContactDialogOpen(true);
                  }}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Create New Contact
                </Button>
              </div>
            )}

            {/* Contacts list */}
            {filteredContacts.map((contact, idx) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                expanded={isMobileView || isContactsExpanded}
                active={activeContactId === contact.id}
                onSelect={handleContactSelect}
                isMobileView={isMobileView}
                ref={
                  idx === Math.max(filteredContacts.length - 10, 0)
                    ? fetchNextPageTriggerObserverRef
                    : null
                }
              />
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Only show expand/collapse button on desktop (non-mobile) */}
      {!isMobileView && (
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleContacts}
          className="absolute right-0 top-24 z-50 hidden size-6 -translate-x-1/2 translate-x-1/2 transform rounded-full border bg-white shadow-sm md:flex"
          aria-label={
            isContactsExpanded ? 'Collapse contacts' : 'Expand contacts'
          }
        >
          {isContactsExpanded ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
      )}
    </div>
  );
}
